import { useState } from 'react'
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material'
import AircraftSelector from './AircraftSelector'
import MaintenanceItemsList from './MaintenanceItemsList'
import { useMutation } from '@apollo/client'
import { CREATE_TASK_RELATIONSHIP } from './queries'

// Relationship type constants
const RELATIONSHIP_TYPES = {
  LINKED: 'LINKED',
  PARENT: 'PARENT',
} as const

interface MaintenanceItem {
  id: string
  title: string
  ataCode: string
  manufactureCode: string
  description: string
  ataMfgCodeNormalized: string
}

const LinkTasksTab = () => {
  const [selectedAircraftId, setSelectedAircraftId] = useState<string | null>(
    null
  )
  const [selectedAircraftOrgSlug, setSelectedAircraftOrgSlug] = useState<
    string | null
  >(null)
  const [selectedTask1, setSelectedTask1] = useState<MaintenanceItem | null>(
    null
  )
  const [selectedTask2, setSelectedTask2] = useState<MaintenanceItem | null>(
    null
  )
  const [linkSuccess, setLinkSuccess] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)

  const [createTaskRelationship, { loading: linkingTasks }] = useMutation(
    CREATE_TASK_RELATIONSHIP,
    {
      onCompleted: () => {
        setLinkSuccess(true)
        setSelectedTask1(null)
        setSelectedTask2(null)
        setLinkError(null)
        setTimeout(() => setLinkSuccess(false), 3000)
      },
      onError: (error) => {
        setLinkError(error.message)
        setLinkSuccess(false)
      },
    }
  )

  const handleAircraftSelect = (
    aircraftId: string | null,
    orgSlug?: string | null
  ) => {
    setSelectedAircraftId(aircraftId)
    setSelectedAircraftOrgSlug(orgSlug || null)
    setSelectedTask1(null)
    setSelectedTask2(null)
  }

  const handleLinkTasks = async () => {
    if (!selectedTask1 || !selectedTask2) return
    await createTaskRelationship({
      variables: {
        input: {
          task1Id: selectedTask1.id,
          task2Id: selectedTask2.id,
          relationship: RELATIONSHIP_TYPES.LINKED,
          isDirectional: false,
        },
      },
    })
  }

  const canLinkTasks =
    selectedTask1 && selectedTask2 && selectedTask1.id !== selectedTask2.id

  return (
    <Grid container spacing={3}>
      {/* Aircraft Selection */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Select Aircraft
            </Typography>
            <AircraftSelector
              selectedAircraftId={selectedAircraftId}
              onAircraftSelect={handleAircraftSelect}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Success/Error Messages */}
      {linkSuccess && (
        <Grid item xs={12}>
          <Alert severity="success">Tasks successfully linked!</Alert>
        </Grid>
      )}

      {linkError && (
        <Grid item xs={12}>
          <Alert severity="error">{linkError}</Alert>
        </Grid>
      )}

      {/* Task Selection */}
      {selectedAircraftId && selectedAircraftOrgSlug && (
        <>
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Select First Task
                </Typography>
                <MaintenanceItemsList
                  aircraftId={selectedAircraftId}
                  orgSlug={selectedAircraftOrgSlug}
                  selectedTask={selectedTask1}
                  onTaskSelect={setSelectedTask1}
                  excludeTaskId={selectedTask2?.id}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Select Second Task
                </Typography>
                <MaintenanceItemsList
                  aircraftId={selectedAircraftId}
                  orgSlug={selectedAircraftOrgSlug}
                  selectedTask={selectedTask2}
                  onTaskSelect={setSelectedTask2}
                  excludeTaskId={selectedTask1?.id}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={2}>
            <Card>
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Actions
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleLinkTasks}
                  disabled={!canLinkTasks || linkingTasks}
                  sx={{ mt: 'auto' }}
                  startIcon={
                    linkingTasks ? <CircularProgress size={20} /> : null
                  }
                >
                  {linkingTasks ? 'Linking...' : 'Link Tasks'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </>
      )}
    </Grid>
  )
}

export default LinkTasksTab
